import { useRef, useState, useEffect, useCallback } from "react";
import { Play, Pause, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";

export const AudioPlayer = ({ src, className, onEnded, compact = false }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setPlaying(false);
    setProgress(0);
    if (audioRef.current) audioRef.current.load();
  }, [src]);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); }
    else { a.pause(); setPlaying(false); }
  }, []);

  const restart = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = 0;
    a.play();
    setPlaying(true);
  }, []);

  if (!src) {
    return <div className="text-xs text-slate-400 font-semibold" data-testid="audio-unavailable">Audio indisponible</div>;
  }

  return (
    <div className={cn("flex items-center gap-3", className)}>
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={(e) => setProgress((e.target.currentTime / (e.target.duration || 1)) * 100)}
        onEnded={() => { setPlaying(false); setProgress(0); onEnded && onEnded(); }}
      />
      <button
        onClick={toggle}
        data-testid="audio-play-pause"
        className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center shadow-[0_4px_0_#5a8ce0] active:translate-y-1 active:shadow-none transition-all shrink-0"
        aria-label={playing ? "Pause" : "Lecture"}
      >
        {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
      </button>
      {!compact && (
        <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-secondary rounded-full transition-[width] duration-200" style={{ width: `${progress}%` }} />
        </div>
      )}
      <button
        onClick={restart}
        data-testid="audio-repeat"
        className="w-10 h-10 rounded-full bg-white border-2 border-slate-200 text-slate-500 flex items-center justify-center hover:bg-slate-50 transition-colors shrink-0"
        aria-label="Répéter"
      >
        <RotateCcw className="w-4 h-4" />
      </button>
    </div>
  );
};
